import React, {useEffect} from 'react';
import SearchControls from '../SearchControls';
import {useSelector} from 'react-redux';
import {Helmet} from 'react-helmet-async';
import IconCheck from '@spothero/icons/check';
import Image from 'common/image/Image';
import COMPONENTS from 'cms/component-list';
import {Page} from 'page/utils';
import trackMidFunnelLanding from 'segment/pages/mid-funnel-landing';
import Map from '../Map';

const City = () => {
    const {
        display_name: name,
        latitude,
        longitude,
        relative_url: cityRelativeUrl,
        slug: citySlug,
    } = useSelector(state => state.city.data);
    const {
        transient_body: transientSlices,
        monthly_body: monthlySlices,
        optional_meta_title_transient: metaTitleTransient,
        optional_meta_description_transient: metaDescriptionTransient,
        optional_meta_title_monthly: metaTitleMonthly,
        optional_meta_description_monthly: metaDescriptionMonthly,
    } = useSelector(state => state.page);
    const {monthly} = useSelector(state => state.searchRequest);
    // check if page is monthly to decide which tab of Prismic content to use
    const slices = monthly ? monthlySlices : transientSlices;
    const metaTitle = monthly ? metaTitleMonthly : metaTitleTransient;
    const metaDescription = monthly
        ? metaDescriptionMonthly
        : metaDescriptionTransient;
    const metaDescriptionDefault = monthly
        ? `Need to book monthly parking in ${name}? Find the best rates for longterm parking near you on SpotHero.`
        : `Looking for a parking spot in ${name}? Find a parking garage near you on SpotHero and get the best rates on parking today.`;
    const canonicalURL = monthly
        ? `/city/monthly/${cityRelativeUrl}`
        : `/city/${cityRelativeUrl}`;

    const formattedSlices = slices
        ? slices.map((slice, key) => {
              const Component = COMPONENTS[slice.slice_type];

              if (Component) {
                  return <Component key={key} slice={slice} />;
              } else {
                  // eslint-disable-next-line no-console
                  console.log(
                      `Error: slice_type of ${slice.slice_type} was not what was expected`
                  );

                  return null;
              }
          })
        : null;

    useEffect(() => {
        trackMidFunnelLanding({
            pageType: Page.CITY,
            city: citySlug,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="City">
            <Helmet>
                <title>
                    {metaTitle ||
                        `${name} ${
                            monthly ? 'Monthly ' : ''
                        }Parking | Book now on SpotHero and save`}
                </title>
                <meta
                    name="description"
                    itemProp="description"
                    content={metaDescription || metaDescriptionDefault}
                />
                <link rel="canonical" href={canonicalURL} />
                <body className="standalone-page" />
            </Helmet>

            <section className="City-hero">
                <div className="City-background">
                    <div className="City-background-overlay" />
                    <Map
                        destination={{
                            title: name,
                            latitude,
                            longitude,
                        }}
                    />
                </div>

                <div className="City-search-controls-container">
                    <SearchControls />

                    <div className="City-details">
                        <div className="City-details-list">
                            <h2>Find and Reserve Parking</h2>
                            <ul>
                                {[
                                    'Fast, Prepaid Reservation',
                                    'Easily Edit/Extend Your Reservation',
                                    'Guaranteed by SpotHero',
                                ].map((detail, n) => (
                                    <li key={n}>
                                        <IconCheck className="Icon-check" />
                                        <div>{detail}</div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="City-trust">
                            <Image
                                cloudinaryImageId="logos/digicert-secured-seal"
                                width={73}
                                height={40}
                                alt="Norton DigiCert Seal"
                            />
                            <Image
                                cloudinaryImageId="front-end/top-rated-app"
                                width={123}
                                height={32}
                                alt="Top Rated App"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <main className="City-main">
                <article className="City-content">{formattedSlices}</article>
            </main>
        </div>
    );
};

export default City;
